import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Calendar, Briefcase, Heart, ArrowRight, Sparkles, Info } from 'lucide-react';
import { validateUserData, sanitizeUserData } from '../utils/validation';

const Onboarding = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState({
    name: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    birth: '',
    occupation: [], // Ahora es un array para múltiples selecciones
    status: '',
    goals: '',
    focusAreas: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const occupations = [
    'empleado/a',
    'emprendedor/a',
    'ventas',
    'estudiante',
    'hogar',
    'creador/a',
    'artista',
    'diseñador/a',
    'escritor/a',
    'músico/a',
    'actor/actriz',
    'fotógrafo/a',
    'profesional independiente',
    'gerente/directivo',
    'consultor/a',
    'coach/mentor',
    'terapeuta',
    'médico/a',
    'abogado/a',
    'ingeniero/a',
    'arquitecto/a',
    'docente',
    'investigador/a',
    'otro'
  ];

  const statusOptions = [
    'soltero/a',
    'en relación',
    'casado/a',
    'divorciado/a',
    'viudo/a'
  ];

  const focusAreas = [
    { id: 'work', label: 'Trabajo/Carrera', icon: '💼' },
    { id: 'love', label: 'Amor/Relaciones', icon: '💞' },
    { id: 'health', label: 'Salud/Bienestar', icon: '🌿' },
    { id: 'spirit', label: 'Espiritualidad', icon: '🔮' },
    { id: 'finance', label: 'Finanzas', icon: '💰' },
    { id: 'family', label: 'Familia', icon: '👨‍👩‍👧‍👦' }
  ];

  const handleInputChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
    
    // Validar en tiempo real
    let isValid = true;
    switch (field) {
      case 'name':
      case 'apellidoPaterno':
      case 'apellidoMaterno':
        isValid = value.trim().length >= 2 && value.trim().length <= 50;
        break;
      case 'birth':
        isValid = /^\d{4}-\d{2}-\d{2}$/.test(value);
        break;
      default:
        isValid = true;
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: isValid ? null : `Campo ${field} inválido`
    }));
  };

  const handleOccupationToggle = (occupation) => {
    setUserData(prev => {
      const currentOccupations = prev.occupation;
      
      if (currentOccupations.includes(occupation)) {
        // Si ya está seleccionada, la removemos
        return {
          ...prev,
          occupation: currentOccupations.filter(occ => occ !== occupation)
        };
      } else if (currentOccupations.length < 2) {
        // Si aún no alcanzamos el máximo (2), la agregamos
        return {
          ...prev,
          occupation: [...currentOccupations, occupation]
        };
      }
      // Si ya hay 2 seleccionadas, no hacemos nada
      return prev;
    });
  };

  const handleFocusAreaToggle = (areaId) => {
    setUserData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas.includes(areaId)
        ? prev.focusAreas.filter(id => id !== areaId)
        : [...prev.focusAreas, areaId]
    }));
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return userData.name.trim() && userData.apellidoPaterno.trim();
      case 2:
        return userData.birth;
      case 3:
        return userData.occupation.length > 0; // Al menos 1 ocupación
      case 4:
        return userData.status;
      case 5:
        return true; // Opcional
      default:
        return false;
    }
  };

  const canProceed = validateStep(step);
  const canGoBack = step > 1;

  const handleNext = () => {
    if (canProceed && step < 5) {
      setStep(step + 1);
    } else if (step === 5) {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (canGoBack) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    
    try {
      // Validar datos antes de procesar
      const validation = validateUserData(userData);
      if (!validation.isValid) {
        alert(`Error de validación: ${validation.errors.join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Sanitizar datos
      const sanitizedData = sanitizeUserData(userData);
      if (!sanitizedData) {
        alert('Error al procesar los datos');
        setLoading(false);
        return;
      }
      
      await onComplete(sanitizedData);
      localStorage.setItem('numerologiaUserData', JSON.stringify(sanitizedData));
      setLoading(false);
    } catch (error) {
      console.error('Error en onboarding:', error);
      alert('Error al procesar los datos. Por favor, intenta de nuevo.');
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-morado flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-morado mb-2">¿Cómo te llamas?</h2>
              <p className="text-gray-600">Cuéntanos tu nombre para personalizar tu experiencia</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre(s)</label>
                <input
                  type="text"
                  value={userData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-morado focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Tu nombre o nombres"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apellido Paterno</label>
                <input
                  type="text"
                  value={userData.apellidoPaterno}
                  onChange={(e) => handleInputChange('apellidoPaterno', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                  placeholder="Tu apellido paterno"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apellido Materno (si aplica)</label>
                <input
                  type="text"
                  value={userData.apellidoMaterno}
                  onChange={(e) => handleInputChange('apellidoMaterno', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                  placeholder="Tu apellido materno (opcional)"
                />
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-morado flex items-center justify-center">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-morado mb-2">¿Cuándo naciste?</h2>
              <p className="text-gray-600">Tu fecha de nacimiento es clave para los cálculos numerológicos</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de nacimiento</label>
              <input
                type="date"
                value={userData.birth}
                onChange={(e) => handleInputChange('birth', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
              />
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-morado flex items-center justify-center">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-morado mb-2">¿A qué te dedicas?</h2>
              <p className="text-gray-600">Selecciona hasta 2 ocupaciones para personalizar tus consejos</p>
              <div className="mt-2 text-sm text-morado font-medium">
                {userData.occupation.length}/2 seleccionadas
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {occupations.map((occupation) => {
                const isSelected = userData.occupation.includes(occupation);
                const isDisabled = !isSelected && userData.occupation.length >= 2;
                
                return (
                  <button
                    key={occupation}
                    onClick={() => handleOccupationToggle(occupation)}
                    disabled={isDisabled}
                    className={`p-3 rounded-xl border text-left transition ${
                      isSelected
                        ? 'border-morado bg-morado/10 text-morado font-medium'
                        : isDisabled
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 hover:border-morado/50'
                    }`}
                  >
                    {occupation}
                    {isSelected && (
                      <span className="ml-2">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-morado flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-morado mb-2">¿Cuál es tu estado civil?</h2>
              <p className="text-gray-600">Para darte orientación personalizada en relaciones</p>
            </div>
            
            <div className="space-y-3">
              {statusOptions.map((status) => (
                <button
                  key={status}
                  onClick={() => handleInputChange('status', status)}
                  className={`w-full p-4 rounded-xl border text-left transition ${
                    userData.status === status
                      ? 'border-morado bg-morado/10 text-morado'
                      : 'border-gray-300 hover:border-morado/50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-morado flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-morado mb-2">¿En qué áreas quieres enfocarte?</h2>
              <p className="text-gray-600">Selecciona las áreas más importantes para ti (opcional)</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {focusAreas.map((area) => (
                <button
                  key={area.id}
                  onClick={() => handleFocusAreaToggle(area.id)}
                  className={`p-4 rounded-xl border text-center transition ${
                    userData.focusAreas.includes(area.id)
                      ? 'border-morado bg-morado/10 text-morado'
                      : 'border-gray-300 hover:border-morado/50'
                  }`}
                >
                  <div className="text-2xl mb-2">{area.icon}</div>
                  <div className="text-sm">{area.label}</div>
                </button>
              ))}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metas personales (opcional)</label>
              <textarea
                value={userData.goals}
                onChange={(e) => handleInputChange('goals', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                placeholder="¿Qué te gustaría lograr en los próximos meses?"
                rows={3}
              />
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-morado/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Paso {step} de 5</span>
            <span>{Math.round((step / 5) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-morado h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {renderStep()}
          <div className="mt-6">
            <div className="flex items-start gap-2 rounded-2xl border border-morado/20 bg-morado/5 px-3 py-3 text-xs text-morado">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                ✨ Revisa con calma cada dato antes de continuar. Por seguridad, la información capturada no puede modificarse dentro de la app.
                Si necesitas un ajuste excepcional, escríbenos a{" "}
                <a href="mailto:info@numerologia-cotidiana.com" className="font-semibold underline decoration-dotted">
                  info@numerologia-cotidiana.com
                </a>{" "}
                y nuestro equipo te ayudará.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-6">
          <button
            onClick={handleBack}
            disabled={!canGoBack}
            className={`px-6 py-3 rounded-xl border transition ${
              canGoBack
                ? 'border-morado text-morado hover:bg-morado/10'
                : 'border-gray-300 text-gray-400 cursor-not-allowed'
            }`}
          >
            Atrás
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed || loading}
            className={`px-6 py-3 rounded-xl text-white transition flex items-center gap-2 ${
              canProceed && !loading
                ? 'bg-morado hover:shadow-lg'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Procesando...
              </>
            ) : step === 5 ? (
              <>
                Comenzar
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Siguiente
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
